import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

/**
 * Google reCAPTCHA v2 ("I'm not a robot" Checkbox) widget.
 *
 * Loads the Google API script once (explicit-render mode) and mounts a checkbox.
 * The parent receives the response token via `onChange` (null when the challenge
 * is cleared, expires, or errors) and can imperatively reset the widget after a
 * failed submit, since reCAPTCHA tokens are single-use.
 */

interface RenderOptions {
  sitekey: string
  callback: (token: string) => void
  'expired-callback': () => void
  'error-callback': () => void
}

interface GreCaptcha {
  render: (container: HTMLElement, options: RenderOptions) => number
  reset: (widgetId?: number) => void
  getResponse: (widgetId?: number) => string
}

declare global {
  interface Window {
    grecaptcha?: GreCaptcha
  }
}

const SCRIPT_ID = 'google-recaptcha-v2-api'
const SCRIPT_SRC = 'https://www.google.com/recaptcha/api.js?render=explicit'

let scriptPromise: Promise<void> | null = null

/** Load the reCAPTCHA API script once and resolve when `grecaptcha.render` is ready. */
function loadRecaptchaScript(): Promise<void> {
  if (window.grecaptcha?.render) return Promise.resolve()
  if (scriptPromise) return scriptPromise

  scriptPromise = new Promise<void>((resolve, reject) => {
    const waitForReady = () => {
      const start = Date.now()
      const interval = setInterval(() => {
        if (window.grecaptcha?.render) {
          clearInterval(interval)
          resolve()
        } else if (Date.now() - start > 10000) {
          clearInterval(interval)
          scriptPromise = null
          reject(new Error('reCAPTCHA failed to initialise'))
        }
      }, 50)
    }

    if (document.getElementById(SCRIPT_ID)) {
      waitForReady()
      return
    }

    const script = document.createElement('script')
    script.id = SCRIPT_ID
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = waitForReady
    script.onerror = () => {
      scriptPromise = null
      reject(new Error('reCAPTCHA failed to load'))
    }
    document.head.appendChild(script)
  })

  return scriptPromise
}

export interface RecaptchaHandle {
  reset: () => void
}

interface RecaptchaProps {
  siteKey: string
  onChange: (token: string | null) => void
  onError?: () => void
}

const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(
  ({ siteKey, onChange, onError }, ref) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const widgetIdRef = useRef<number | null>(null)

    useImperativeHandle(ref, () => ({
      reset: () => {
        if (widgetIdRef.current !== null && window.grecaptcha) {
          window.grecaptcha.reset(widgetIdRef.current)
          onChange(null)
        }
      },
    }))

    useEffect(() => {
      let cancelled = false

      loadRecaptchaScript()
        .then(() => {
          if (cancelled || !containerRef.current || widgetIdRef.current !== null) return
          widgetIdRef.current = window.grecaptcha!.render(containerRef.current, {
            sitekey: siteKey,
            callback: (token: string) => onChange(token),
            'expired-callback': () => onChange(null),
            'error-callback': () => {
              onChange(null)
              onError?.()
            },
          })
        })
        .catch(() => onError?.())

      return () => {
        cancelled = true
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [siteKey])

    return <div ref={containerRef} />
  },
)

Recaptcha.displayName = 'Recaptcha'

export default Recaptcha
